import { jsx } from '@amazon/vinyl-tsx'
import { combineData, data } from '@amazon/vinyl-observable'
import { attachVideoToStage, getHasVideo, getInPip, getVideoElement } from '@/video/videoStage'
import type { Player } from '@/playback/api'
import type { PlaylistController } from '@/playback/PlaylistController'
import { Icon } from '@/components/Icon'
import { ImageView } from '@/components/ImageView'
import { createImageModel } from '@/util/ImageUrl'
import { ImageSizes } from '@/style/styleCommon'
import { LoadingIndicator } from '@/components/LoadingIndicator'

export interface VideoStageOverlayDeps {
    readonly playerPromise: Promise<Player>
    readonly playlistController: PlaylistController
}

export interface VideoStageOverlayProps {
    readonly deps: VideoStageOverlayDeps
}

/**
 * Full-bleed video stage that hosts the singleton `<video>` whenever
 * a video is loaded. Mounted at the AppView root so it sits above
 * the route container and below the header / transport. The stage
 * is hidden while picture-in-picture is active (the OS chrome owns
 * the picture).
 *
 * The `<video>` element fills the stage. Click toggles play/pause.
 * Three controls sit on top of the video:
 *   - close (top-left, caret-down): clears the playlist
 *   - PiP   (top-right, just inside fullscreen)
 *   - fullscreen (top-right): fullscreens a wrapper that contains the
 *     stage + transport so users still see playback controls
 *
 * Loading overlay: while the player is buffering / before metadata
 * lands, we render the entry's artwork as a poster with a spinner
 * over it. Hides once the player can play through.
 */
export function VideoStageOverlay(props: VideoStageOverlayProps) {
    const { playlistController } = props.deps
    const isFullscreen = data(false)
    const isLoading = data(true)

    const showStage = combineData({
        hasVideo: getHasVideo(),
        inPip: getInPip(),
    }).map(({ hasVideo, inPip }) => hasVideo && !inPip)

    const currentEntry = playlistController.current
    const posterImage = currentEntry.map((e) =>
        createImageModel(e?.image, { scaleToSquare: ImageSizes.LARGE * 2 })
    )

    function togglePlayPause() {
        props.deps.playerPromise
            .then((p) => {
                if (p.paused) p.play().catch(() => {})
                else p.pause()
            })
            .catch(() => {})
    }

    function close() {
        playlistController.clear()
    }

    async function togglePip() {
        const video = getVideoElement()
        if (document.pictureInPictureElement === video) {
            await document.exitPictureInPicture().catch(() => {})
        } else {
            await video.requestPictureInPicture().catch(() => {})
        }
    }

    function toggleFullscreen(target: HTMLElement) {
        if (document.fullscreenElement) {
            document.exitFullscreen().catch(() => {})
        } else {
            target.requestFullscreen().catch(() => {})
        }
    }

    const stage = (
        <div
            className="videoStageOverlay"
            visible={showStage}
            onConnect={(stageEl) => {
                const detach = attachVideoToStage(stageEl)
                const onFs = () => {
                    isFullscreen.value = !!document.fullscreenElement
                }
                document.addEventListener('fullscreenchange', onFs)

                // Clicking anywhere on the stage that isn't a control
                // toggles play/pause. Listen on capture so the
                // singleton <video>'s native click bubbles up first.
                const onClick = (e: MouseEvent) => {
                    const target = e.target as HTMLElement
                    if (target.closest('.videoStageBtn')) return
                    togglePlayPause()
                }
                stageEl.addEventListener('click', onClick)

                // Loading overlay: hide once the player can play
                // through. Show again on each new track.
                let timeUpdateSub: (() => void) | null = null
                let canPlaySub: (() => void) | null = null
                const entrySub = currentEntry.onData(() => {
                    isLoading.value = true
                })
                props.deps.playerPromise
                    .then((p) => {
                        canPlaySub = p.on('readyStateChange', () => {
                            if (p.canPlayThrough) isLoading.value = false
                        })
                        timeUpdateSub = p.on('timeUpdate', () => {
                            if (p.currentTime > 0) isLoading.value = false
                        })
                    })
                    .catch(() => {})

                return () => {
                    document.removeEventListener('fullscreenchange', onFs)
                    stageEl.removeEventListener('click', onClick)
                    entrySub()
                    timeUpdateSub?.()
                    canPlaySub?.()
                    detach()
                }
            }}
        >
            <button
                className="videoStageBtn videoCloseBtn"
                title="Close"
                aria-label="Close video"
                onclick={close}
            >
                <Icon icon="caretDown" />
            </button>
            <div className="videoStageActions">
                <button
                    className="videoStageBtn pipBtn"
                    title="Picture in picture"
                    aria-label="Picture in picture"
                    onclick={() => {
                        togglePip().catch(() => {})
                    }}
                >
                    <Icon icon="pip" />
                </button>
                <button
                    className="videoStageBtn fullscreenBtn"
                    title="Fullscreen"
                    aria-label="Fullscreen"
                    onclick={() => {
                        // Fullscreen the AppView so the transport
                        // stays visible alongside the picture; see
                        // the wrapper id below.
                        const target =
                            document.querySelector<HTMLElement>('#fullscreenRoot') ?? stage
                        toggleFullscreen(target)
                    }}
                >
                    <Icon
                        icon="fullscreen"
                        className="fullscreenIcon"
                        visible={isFullscreen.map((v) => !v)}
                    />
                    <Icon
                        icon="fullscreenExit"
                        className="fullscreenExitIcon"
                        visible={isFullscreen}
                    />
                </button>
            </div>
            <div
                className="videoLoadingOverlay"
                onConnect={(el) => {
                    return isLoading.onData((loading) => {
                        el.style.display = loading ? '' : 'none'
                    })
                }}
            >
                <ImageView className="videoLoadingPoster" image={posterImage} />
                <LoadingIndicator className="videoLoadingSpinner" />
            </div>
        </div>
    )
    return stage
}
